import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { BaseConnectionService } from "../../../services/connections/baseConnectionService";
import { SystemEntityCollections } from "../../../shared/enums/shared-enums";
import { BaseConnection } from "../../../types/connection";
import { enqueueSnackbar, SnackbarAction } from "notistack";
import {
  getErrorMessage,
  useEntityQueryClient,
  usePaginatedDataQuery,
  Pagination,
} from "../entityQueryClient";

export interface CreateEdgeData<T extends BaseConnection> {
  _from: string;
  _to: string;
  metadata?: Partial<Omit<T, "_from" | "_to">>;
}

interface ConnectionQueryClientParams<T extends BaseConnection> {
  service: BaseConnectionService<T>;
  collection: SystemEntityCollections;
  snackbarDisplayName?: (data: T) => string;
  snackbarAction?: (data: T) => SnackbarAction;
  defaultConnection?: T;
}

const useConnectionQueryClient = <T extends BaseConnection, F = T>({
  service,
  collection,
  snackbarDisplayName,
  defaultConnection,
  snackbarAction,
}: ConnectionQueryClientParams<T>) => {
  const useFetchConnectedEdges = (
    entityId: string,
    direction: "outbound" | "inbound",
    {
      bodyParams = {},
      enabled = true,
      externalPagination,
      externalSetPagination,
    }: {
      bodyParams?: Partial<F>;
      enabled?: boolean;
      externalPagination?: Pagination;
      externalSetPagination?: React.Dispatch<React.SetStateAction<Pagination>>;
    } = {}
  ) => {
    const hasEntityId = Boolean(entityId);

    return usePaginatedDataQuery<T, F>({
      collection,
      queryKey: ["connectedEdges", entityId, direction],
      bodyParams,
      defaultEntity: defaultConnection,
      enabled: hasEntityId && enabled,
      externalPagination,
      externalSetPagination,
      queryFn: ({ offset, limit, sort, params }) =>
        service.fetchConnectedEdges(
          entityId,
          direction,
          offset,
          limit,
          sort,
          params
        ),
    });
  };

  const useCreate = () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (data: CreateEdgeData<T>) => service.createEdge(data),
      onError: (error: Error | AxiosError) => {
        console.error(`Error creating ${collection} connection:`, error);
        const message = getErrorMessage(
          error,
          `Failed to create ${collection} connection.`
        );
        enqueueSnackbar(message, {
          variant: "error",
        });
      },
      onSuccess: (data: T) => {
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) && query.queryKey[0] === collection,
        });
        enqueueSnackbar(
          `Successfully created ${snackbarDisplayName ? snackbarDisplayName(data) : `${collection} connection`}.`,
          {
            variant: "success",
            action: snackbarAction ? snackbarAction(data) : undefined,
          }
        );
      },
    });
  };

  return {
    ...useEntityQueryClient<T, F>({
      service,
      collection,
      defaultEntity: defaultConnection,
      snackbarDisplayName,
      snackbarAction,
    }),
    useFetchConnectedEdges,
    useCreate,
  };
};

export default useConnectionQueryClient;
